
import { defineComponent, ref } from 'vue';
import _ from 'lodash';

import useApp, { SearchType, SuggestionResult } from '@/composables';
import CityIcon from '@/assets/img/city_icon.svg';

import ATSPanel from './ATSPanel.vue';
import PBLPanel from './PBLPanel.vue';

const SEARCH_DELAY_TIME = 300;

export default defineComponent({
    name: 'Search',
    components: {
        ATSPanel,
        PBLPanel
    },
    setup() {
        const { state, clearOpenSite, getSearchSuggestions, doSearch, clearSearch, loadSuggestionResult, toggleFilterStatus } = useApp();

        const MAX_RESULTS = 10;
        const el = ref(null);
        const search = ref('');
        const selectedSuggestionIndex = ref<number>(-1);
        const isSearching = ref<boolean>(false);
        const isFocused = ref<boolean>(false);
        const selectedSearchType = ref<SearchType>('');
        const searchSuggestions = ref<SuggestionResult[]>([]);
        const inputRef = ref<HTMLInputElement>();

        const searchTypes = [
            { type: 'ats', title: 'Alberta Township System (ATS) Format', example: 'W4-27-38-11-NW, W4-3-40-3-SE-2', withIcon: true },
            { type: 'pbl', title: 'Plan, Block, Lot (PBL) Format', example: '421429-1-2, 5826CL-8-3', withIcon: true },
        ];

        const selectSearchType = (type: SearchType) => {
            if (selectedSearchType.value === type) {
                selectedSearchType.value = '';
            } else {
                selectedSearchType.value = type;
            }
        };

        const clearForm = () => {
            search.value = '';
            searchSuggestions.value = [];
            clearSearch();
        };

        const selectResult = (result: SuggestionResult) => {
            if (result.type === 'Municipality' || result.type === 'Neighborhood') {
                doSearch(result.title, result.type);
                selectedSuggestionIndex.value = -1;
                isFocused.value = false;
                searchSuggestions.value = [];
                search.value = result.title;
            } else if (result.item) {
                selectedSuggestionIndex.value = -1;
                isFocused.value = false;
                searchSuggestions.value = [];
                search.value = result.title;
                loadSuggestionResult(result);
            }
        };

        const handleClickOutside = (e: MouseEvent) => {
            e.stopPropagation();
            if ((e.target as any).type === 'text') {
                return;
            }
            selectedSuggestionIndex.value = -1;
            isFocused.value = false;
        };

        const onArrowDown = () => {
            if (selectedSuggestionIndex.value < searchSuggestions.value.length) {
                selectedSuggestionIndex.value += 1;
            }
        };

        const onArrowUp = () => {
            if (selectedSuggestionIndex.value > 0) {
                selectedSuggestionIndex.value -= 1;
            } else {
                selectedSuggestionIndex.value = -1;
            }
        };

        const onEnter = () => {
            isFocused.value = false;
            inputRef.value?.blur();
            clearOpenSite();

            // when pressing enter on a suggestion item
            if (selectedSuggestionIndex.value !== -1 && !isSearching.value) {
                loadSuggestionResult(searchSuggestions.value[selectedSuggestionIndex.value]);
            } else {
                isSearching.value = true;
                doSearch(search.value, selectedSearchType.value);
                isSearching.value = false;
            }
        };

        const onFocus = () => {
            isFocused.value = true;
        };

        const highlighSearchResult = (title: string) => {
            const startIndex = title.toLowerCase().indexOf(search.value.toLowerCase());
            const exactOccurence = title.slice(startIndex, startIndex + search.value.length);

            return title.replace(exactOccurence, `<span class="highlightResults">${exactOccurence}</span>`);
        };

        const handleInputChange = _.debounce((event: InputEvent) => {
            if (search.value.length === 0) {
                clearForm();
                return;
            }

            if (!isFocused.value) {
                isFocused.value = true;
            }

            isSearching.value = true;
            selectedSearchType.value = '';
            searchSuggestions.value = getSearchSuggestions(search.value, selectedSearchType.value, MAX_RESULTS);
            setTimeout(() => {
                isSearching.value = false;
            }, 300);
        }, SEARCH_DELAY_TIME);

        const atsPblSearch = (query: string) => {
            search.value = query;
            onEnter();
            selectedSearchType.value = '';
        };

        return {
            el,
            searchTypes,
            selectedSearchType,
            search,
            isSearching,
            isFocused,
            selectedSuggestionIndex,
            searchSuggestions,
            inputRef,
            state,
            // funcs
            selectSearchType,
            handleClickOutside,
            onArrowDown,
            onArrowUp,
            onEnter,
            onFocus,
            clearForm,
            highlighSearchResult,
            handleInputChange,
            selectResult,
            atsPblSearch,
            toggleFilterStatus,
            CityIcon
        };
    }
});
